import React from 'react';
import { colors, typography } from './tokens';

export type InfoPillVariant = 'link' | 'status';

export interface InfoPillProps {
  variant?: InfoPillVariant;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  showIcon?: boolean;
}

const variantStyles: Record<InfoPillVariant, {
  backgroundColor: string;
  color: string;
  borderColor: string;
}> = {
  link: {
    backgroundColor: '#179DB5', // Tertiary/100 from Figma
    color: colors.text.neutral.onNeutral, // #F3F3F3
    borderColor: '#179DB5',
  },
  status: {
    backgroundColor: colors.status.positive, // #169959
    color: colors.text.positive.onPositive, // #EBFFEE
    borderColor: colors.status.positive,
  },
};

export const InfoPill: React.FC<InfoPillProps> = ({ 
  variant = 'status', 
  children,
  className = '',
  onClick,
  showIcon = false,
}) => {
  const styles = variantStyles[variant];
  const isClickable = onClick !== undefined;
  
  return (
    <div
      className={`${className} ${isClickable ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      style={{
        backgroundColor: styles.backgroundColor,
        color: styles.color,
        borderColor: styles.borderColor,
        border: 'none',
        borderRadius: '9999px',
        padding: showIcon && variant === 'link' ? '4px 4px 4px 12px' : '4px 12px',
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.medium,
        fontFamily: typography.fontFamily.primary,
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: showIcon ? '8px' : '0',
        margin: 0,
        whiteSpace: 'nowrap',
      }}
    >
      <span>{children}</span>
      {showIcon && variant === 'link' && (
        <div
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '7.5px',
            width: '15px',
            height: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: typography.fontSize.xs,
            fontWeight: typography.fontWeight.medium,
            color: colors.text.neutral.onNeutral,
          }}
        >
          ↗
        </div>
      )}
    </div>
  );
};

// Convenience components for common use cases
export const LinkPill: React.FC<Omit<InfoPillProps, 'variant'>> = (props) => (
  <InfoPill {...props} variant="link" showIcon />
);

export const StatusPillInfo: React.FC<Omit<InfoPillProps, 'variant' | 'showIcon'>> = (props) => (
  <InfoPill {...props} variant="status" />
);